import { useEffect, useState } from "react";
import { useGlobalState } from "senf-shared";
import { Map } from "mapbox-gl";

export const useLabels = (sourceId = "gisLabels", featureId = null) => {
  const [source, setSource] = useState(null);
  const [points, setPoints] = useState(null);
  const [translation] = useGlobalState().translationsState;

  // Define a function to adjust feature properties.
  // This makes the code more DRY (Don't Repeat Yourself).
  const adjustFeatureProperties = (feature, translation) => ({
    ...feature,
    properties: {
      ...feature.properties,
      tooltipLabel:
        feature.properties.tooltipLabel === "none"
          ? ""
          : translation?.[feature.properties.tooltipLabel] ||
            feature.properties.tooltipLabel ||
            feature.properties.number,
    },
  });

  // Define a function to adjust the data structure based on its type.
  const adjustDataBasedOnType = (points, translation) => {
    if (points?.type === "FeatureCollection" || points?.features) {
      // Directly adjust if points itself is a FeatureCollection
      return {
        type: "FeatureCollection",
        features: points.features.map((feature) =>
          adjustFeatureProperties(feature, translation)
        ),
      };
    }
    if (Array.isArray(points)) {
      // Process each element in the array if points is an array
      const allFeatures = points.reduce((acc, point) => {
        const data = point?.data ?? point?.[0]?.data ?? point;

        // Skip the point if data is undefined or null
        if (!data) {
          return acc;
        }

        if (data?.type === "FeatureCollection" || data?.features) {
          const adjustedFeatures = data.features.map((feature) =>
            adjustFeatureProperties(feature, translation)
          );
          acc.push(...adjustedFeatures); // Spread and push each feature individually
        }

        return acc;
      }, []);

      // Create a new FeatureCollection from all adjusted features
      return {
        type: "FeatureCollection",
        features: allFeatures,
      };
    }
    // Return points as is if it's neither an array nor a FeatureCollection
    return points;
  };

  useEffect(() => {
    if (source && points) {
      if (featureId) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
        return;
      }
      const adjustedData = adjustDataBasedOnType(points, translation);

      if (adjustedData?.features?.length) {
        source.setData(adjustedData);
      } else {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
      }
    }
    if (source && !points) {
      source.setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }, [source, points, translation, featureId]);

  const setLayer = (map: Map) => {
    // After the map style has loaded on the page,
    // add a source layer and default styling for a single point
    map.on("load", () => {
      let s = map.getSource(sourceId);
      if (!s) {
        map.addSource(sourceId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        map.addLayer({
          id: sourceId,
          type: "symbol", // or another type appropriate for your data
          source: sourceId,

          layout: {
            "text-field": ["get", "tooltipLabel"],
            "text-size": 11, // Controlling the font size
            "text-anchor": [
              "case",
              [
                "all",
                ["==", ["geometry-type"], "Polygon"],
                ["!", ["has", "icon"]],
              ],
              "center", // If it's a polygon without an icon, center the text
              "left", // Otherwise, align to the left
            ],
            "text-offset": [
              "case",
              [
                "all",
                ["==", ["geometry-type"], "Polygon"],
                ["!", ["has", "icon"]],
              ],
              ["literal", [0, 0]],
              [
                "case",
                ["has", "icon"],
                ["literal", [1.4, 0]],
                ["literal", [0.9, 0]],
              ],
            ], // Controlling the offset based on geometry type, icon presence, and polygon without icon
            "text-font": ["Nunito Bold"],
            //   "text-font": ["Nunito Bold"],
            // Additional layout properties...
            "text-max-width": 20, // To ensure text does not exceed certain length
          },
          paint: {
            "text-color": ["coalesce", ["get", "labelColor"], ["get", "color"]],
            "text-halo-color": "white",
            "text-halo-width": 1, // Providing a white text halo for readability
            "text-opacity": 1, // You may control the text opacity
          },
        });

        s = map.getSource(sourceId);
      }

      setSource(s);
    });

    // setTimeout(() => {
    //   map?.moveLayer(sourceId, "transit-label");
    // }, 100);
  };

  return [setLayer, setPoints];
};
